import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  CircularProgress,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { supabase, logRequestDetails } from '../supabaseClient';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [alertInfo, setAlertInfo] = useState({ message: '', severity: 'error' });

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate('/workflows'); // Redirect if already logged in
      }
    };
    checkSession();
  }, [navigate]); // Add navigate as a dependency

  useEffect(() => {
    if (location?.state?.message) {
      setAlertInfo({
        message: location.state.message,
        severity: location.state.severity || 'error'
      });
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        throw new Error('Missing Supabase environment variables');
      }
      
      await logRequestDetails('auth.signInWithPassword');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) throw error;
      if (data.user) {
        const returnUrl = location?.state?.returnUrl || '/workflows';
        navigate(returnUrl);
      }
    } catch (error) {
      let errorMessage = 'Giriş sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      if (error.message === 'Failed to fetch' || error.message.includes('ERR_NAME_NOT_RESOLVED')) {
        errorMessage = 'Ağ hatası. Lütfen internet bağlantınızı kontrol edin.';
      } else if (error.message === 'Missing Supabase environment variables') {
        errorMessage = 'Uygulama yapılandırma hatası. Lütfen destek ekibiyle iletişime geçin.';
      } else if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'Geçersiz e-posta veya şifre. Lütfen tekrar deneyin.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/workflows`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      
      if (error) throw error;
      // Don't navigate here - let the OAuth redirect handle it
    } catch (error) {
      setError(error.message);
    }
  };

  const { theme } = useThemeMode();

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: theme => theme.palette.mode === 'dark' 
        ? '#1A1A2A' 
        : '#f5f7fa',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        background: theme => theme.palette.mode === 'dark'
          ? `radial-gradient(circle at center, rgba(0, 74, 173, 0.15) 0%, transparent 50%),
             repeating-linear-gradient(45deg, rgba(0, 74, 173, 0.08) 0px, rgba(0, 74, 173, 0.08) 2px, transparent 2px, transparent 10px),
             repeating-linear-gradient(-45deg, rgba(0, 74, 173, 0.08) 0px, rgba(0, 74, 173, 0.08) 2px, transparent 2px, transparent 10px)`
          : `radial-gradient(circle at center, rgba(0, 74, 173, 0.08) 0%, transparent 50%),
             repeating-linear-gradient(45deg, rgba(0, 74, 173, 0.03) 0px, rgba(0, 74, 173, 0.03) 2px, transparent 2px, transparent 10px),
             repeating-linear-gradient(-45deg, rgba(0, 74, 173, 0.03) 0px, rgba(0, 74, 173, 0.03) 2px, transparent 2px, transparent 10px)`,
        backgroundSize: '100% 100%, 20px 20px, 20px 20px',
        animation: 'gradientMove 20s ease-in-out infinite',
        zIndex: 0,
      },
      '@keyframes gradientMove': {
        '0%, 100%': { 
          backgroundPosition: '0% 0%, 0px 0px, 0px 0px',
          opacity: 0.8
        },
        '50%': { 
          backgroundPosition: '0% 0%, 10px 10px, -10px -10px',
          opacity: 1
        }
      }
    }}>
      <Container maxWidth="xs" sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '440px' },
        mx: 'auto',
        px: { xs: 1, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 2, sm: 3 },
            margin: { xs: 1, sm: 2 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: '24px',
            backgroundColor: theme => theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            border: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: theme => `0 4px 24px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}
        >
          <Typography 
            component="h1" 
            variant="h5" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: 'primary.main',
              fontWeight: 600
            }}>
            Giriş Yap
          </Typography>

          <form onSubmit={handleLogin} style={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-posta Adresi"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Şifre"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />

            {(error || alertInfo.message) && (
              <Alert 
                severity={alertInfo.message ? alertInfo.severity : 'error'} 
                sx={{ mt: 2 }}
              >
                {error || alertInfo.message}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: '8px',
                fontWeight: 600
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Giriş Yap'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              sx={{ 
                mb: 2,
                borderRadius: '8px',
                py: 1.5,
                fontWeight: 600
              }}
            >
              Google ile Devam Et
            </Button>

            <Box 
              sx={{ 
                mt: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 1.5,
                  '&::before, &::after': {
                    content: '""',
                    flex: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    px: 1
                  }}
                >
                  veya
                </Typography>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1.5, sm: 2 }, 
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                px: { xs: 1, sm: 2 }
              }}>
                <Link 
                  to="/signup" 
                  style={{ textDecoration: 'none', width: '100%' }}
                >
                  <Button
                    variant="text"
                    fullWidth
                    sx={{
                      background: 'transparent',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      position: 'relative',
                      color: 'text.primary',
                      py: 1,
                      fontWeight: 600,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: '0%',
                        height: '2px',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'primary.main',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover': {
                        background: 'transparent',
                        '&::after': {
                          width: '80%'
                        }
                      }
                    }}
                  >
                    Hesap Oluştur
                  </Button>
                </Link>

                <Link 
                  to="/reset-password" 
                  style={{ textDecoration: 'none', width: '100%' }}
                >
                  <Button
                    variant="text"
                    fullWidth
                    sx={{
                      background: 'transparent',
                      position: 'relative',
                      color: 'text.primary',
                      py: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 600,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: '0%',
                        height: '2px',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'primary.main',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover': {
                        background: 'transparent',
                        '&::after': {
                          width: '80%'
                        }
                      }
                    }}
                  >
                    Şifremi Unuttum
                  </Button>
                </Link>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
    </ThemeProvider>
  );
}
